import type { ReactNode } from 'react';

/**
 * Figures that should be read at a glance (ADR-0057): an HTTP answer as a
 * coloured badge, a small line of a figure's recent history, and a figure's
 * tile — the number, its change, the line — the way an SEO overview shows them.
 */

const HTTP_MEANING: Record<string, string> = {
  healthy: 'страница отвечает',
  maintenance: 'перенаправление',
  degraded: 'ошибка запроса: страницы нет или доступ закрыт',
  down: 'ошибка сервера',
  unknown: 'сервер не ответил',
};

/** 2xx green, 3xx blue, 4xx amber, 5xx red; no answer grey. */
export function httpTone(code: number | null): 'healthy' | 'maintenance' | 'degraded' | 'down' | 'unknown' {
  if (code === null || code === 0) return 'unknown';
  if (code >= 500) return 'down';
  if (code >= 400) return 'degraded';
  if (code >= 300) return 'maintenance';
  return 'healthy';
}

/** An HTTP answer as a badge: «200» green, «404» amber, «502» red. */
export function HttpStatus({ code, prefix = false }: { readonly code: number | null; readonly prefix?: boolean }): ReactNode {
  const tone = httpTone(code);
  return (
    <span className={`http-badge http-badge-${tone}`} title={`${code ?? ''} ${HTTP_MEANING[tone]}`.trim()}>
      {code === null || code === 0 ? 'нет ответа' : `${prefix ? 'HTTP ' : ''}${code}`}
    </span>
  );
}

/** A figure's recent history as a small line, no axes: the shape, not the values. */
export function Sparkline({ values, label, invert = false }: { readonly values: readonly number[]; readonly label: string; readonly invert?: boolean }): ReactNode {
  if (values.length < 2) return null;
  const width = 120;
  const height = 32;
  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = high - low || 1;
  const points = values.map((value, index) => {
    const x = (index / (values.length - 1)) * width;
    const share = (value - low) / span;
    const y = 3 + (invert ? share : 1 - share) * (height - 6);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg className="sparkline" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label} preserveAspectRatio="none">
      <polyline fill="none" points={points.join(' ')} />
    </svg>
  );
}

/**
 * One figure as a tile: its name and source, the number, how it moved and a
 * small line of its history. `better` says which way is good — down for a
 * search position or a spam score.
 */
export function MetricTile({
  label,
  source,
  value,
  unit,
  delta,
  better = 'up',
  history,
  footnote,
  onOpen,
}: {
  readonly label: string;
  /** Where the figure comes from: a service's mark and name. */
  readonly source?: ReactNode;
  readonly value: string | null;
  readonly unit?: string | undefined;
  /** The change over the period the footnote names; null when there is nothing to compare. */
  readonly delta?: { readonly value: number; readonly text: string } | null;
  readonly better?: 'up' | 'down';
  readonly history?: readonly number[];
  readonly footnote?: ReactNode | undefined;
  /** A tile that leads somewhere: the tab with the detail. */
  readonly onOpen?: () => void;
}): ReactNode {
  const good = delta ? (better === 'up' ? delta.value > 0 : delta.value < 0) : false;
  const body = (
    <>
      <span className="metric-tile-head">
        <span className="metric-tile-label">{label}</span>
        {source ? <span className="metric-tile-source">{source}</span> : null}
      </span>
      <span className="metric-tile-value">
        {value ?? <span className="faint">—</span>}
        {value !== null && unit ? <span className="metric-tile-unit">{unit}</span> : null}
      </span>
      <span className="metric-tile-foot">
        {delta && delta.value !== 0 ? <span className={good ? 'tone-healthy' : 'tone-down'}>{delta.text}</span> : null}
        {footnote ? <span className="muted">{footnote}</span> : null}
      </span>
      {history && history.length > 1 ? <Sparkline values={history} label={`${label}: история`} invert={better === 'down'} /> : null}
    </>
  );
  return onOpen ? (
    <button type="button" className="metric-tile metric-tile-link" onClick={onOpen}>
      {body}
    </button>
  ) : (
    <div className="metric-tile">{body}</div>
  );
}
